<script lang="ts">
    import I18n, {locale, t} from 'i18n-js';
    import {createEventDispatcher, onMount} from 'svelte';
    import Information from './Information.svelte';
    import {computeSeats, itemReady} from '../../product';
    import type {ReadyInStock, Availability, CountryNames, Options, Variables, Product} from '../../product';
    import {formatReadyness} from '../../i18n';
    import {eventCartAcceptAlternative, eventCartDecrement, eventCartIncrement, eventCartOfferAlternative, eventCartRejectAlternative} from '../../matomo';
    import {offerBetterAlternative} from './better-alternative';
    import type {BetterAlternative} from './better-alternative';
    import {currentCountry as country} from '../../svelte/current-country';
    import {isPhone} from '../../svelte/useragent';

    const SCOPE = { scope: 'Options.Choose' }; // zum Einbinden der Locales

    export let ITEMS: Array<V1.Product>;
    export let VARIABLES: Variables | null;
    export let COUNTRY_NAMES: CountryNames;
    export let AVAILABILITY: Availability;
    export let READY_IN_STOCK: ReadyInStock;
    export let FEW_IN_STOCK: number;
    export let PRODUCT: Product;

    const values: {[key: string]: string | undefined} = {};

    export let payload: string | undefined = undefined;
    export let amount: number | false = 1;

    const customizing: {[dimension: string]: boolean} = {};

    const showInformation: {[dimension: string]: boolean} = {};

    onMount(async () => {
        if(!payload) {
            const keys = [];
            for(const dimension in VARIABLES) {
                const variable = VARIABLES[dimension];
                keys.push(splitOptions(variable.options).fixed[0].key);
            }
            payload = keys.join('-');
        }
        await import('../../yoocon-shop');
        setTimeout(() => {
            updatePayload(payload);
            updateValues(values);
            itemChanged(item, amount || 1);
        });
    });

    function splitOptions(options: Options) {
        const fixed = [], custom = [];
        for(const key in options) {
            if(!options[key])
                continue;
            const option = {key, title: options[key]};
            if(key[key.length-1] === '*')
                custom.push(option);
            else
                fixed.push(option);
        }
        return {fixed, custom};
    }

    const dispatch = createEventDispatcher();

    function updatePayload(value: string | undefined) {
        if(!value) return;
        const keys = value.split('-');
        for(const dimension in VARIABLES) {
            const key = keys.shift()?.replace('*', '');
            if(key && values[dimension] !== key)
                values[dimension] = key;
        }
    }

    function updateValues(values: {[key: string]: string | undefined}) {
        let keys: Array<string> | undefined = [];
        for(const dimension in values) {
            let key: string | undefined = values[dimension];
            if(key) {
                if(VARIABLES?.[dimension].options[key + '*'])
                    key += '*';
                else if(!VARIABLES?.[dimension].options[key])
                    key = undefined;
            }
            if(!key) {
                keys = undefined;
                break;
            }
            keys.push(key);
        }
        const value = keys?.join('-');
        if(payload !== value)
            payload = value;
    }

    let betterAlternative: BetterAlternative | false | undefined;

    function selectBetterAlternative() {
        if(betterAlternative)
            payload = betterAlternative.altPayload.value;
    }

    let dimensions: {
        key: 'whd' | 'lwh' | 'lw' | 'wh' | 'wlh' | 'hd',
        title: string,
    } | undefined;

    let additional: {title?: string, text: string} | undefined;

    function itemChanged(item: V1.Product | undefined, amount: number) {
        dispatch('update', {item, amount});

        if(!item) {
            dimensions = additional = undefined;
            if(betterAlternative)
                betterAlternative = undefined;
            return;
        }

        if(PRODUCT.dimensions) {
            let title = PRODUCT.dimensions.title;
            if(!title) {
                const matches = item.subtitle.split(',')[0].match(/(\d.*)/);
                if(matches)
                    title = matches[1];
            }
            if(title)
                dimensions = {key: PRODUCT.dimensions.key as any, title};
        }

        if(PRODUCT.additional) {
            additional = PRODUCT.additional[locale];
        }

        const {item_no} = ITEMS[0];

        if(item_no == 'curtain-rail') {
            if(betterAlternative !== false)
                betterAlternative = offerBetterAlternative(ITEMS, VARIABLES, item, amount, 10, 4);
        }

        if(item_no == 'all-oak-shelf') {
            const excessHeight = parseInt(values.boards as any) > 2 ? 20 : 0;
            const segments = Math.floor((parseInt(values.width as any) - 60) / 70) + 1;
            additional = {
                text: t('shelf-segments', {count: segments, ...SCOPE}) + ", " +
                    (excessHeight ?
                        t('shelf-excess-height', {length: excessHeight, ...SCOPE}) :
                        t('shelf-no-excess-height', SCOPE))
            };
        }

        if(item_no == 'alu-wall-shelf') {
            const boards = parseInt(values.boards as any);
            const height = [30, 70, 110, 150][boards - 1];
            const depth = I18n.toNumber(26.5, {precision: 1});
            dimensions!.title = `${values.width}x${height}x${depth}cm`;
        }

        if(dimensions) {
            dimensions.title = dimensions.title
                .split(/\s*x\s*/)
                .map(s => s.replace(/(\d+)\s*(\D+)$/, '$1\u00a0$2'))
                .join('\u00a0×\u00a0');
        }
    }

    function sendInquiry() {
        const {title} = ITEMS[0];
        const subject = t('inquiry.subject', {title, ...SCOPE});
        const body = t(
            PRODUCT.inquiry === 'limited-edition' ? 'inquiry.body-limited-edition' : 'inquiry.body',
            {title, ...SCOPE}
        );
        const to = 'hello@nutsandwoods.de';
        const url = `mailto:${encodeURIComponent(to)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
        location.href = url;
    }

    (window as any).sendProductInquiry = sendInquiry;

    function decrementAmount() {
        amount = Math.max(1, amount as number - 1);
        eventCartDecrement(item ?? ITEMS[0], item?.payload, amount as number);
    }

    function incrementAmount() {
        amount = amount as number + 1;
        eventCartIncrement(item ?? ITEMS[0], item?.payload, amount as number);
    }

    function showShippingPopup() {
        dispatch('shipping');
    }

    function eventOfferAlternative() {
        eventCartOfferAlternative(item, parseInt((betterAlternative as BetterAlternative).altSize));
    }

    function eventAcceptAlternative() {
        eventCartAcceptAlternative(item, parseInt((betterAlternative as BetterAlternative).altSize));
    }

    function eventRejectAlternative() {
        eventCartRejectAlternative(item, parseInt((betterAlternative as BetterAlternative).altSize));
    }

    function selectInputText(e: Event) {
        const el = e.target! as HTMLInputElement;
        el.select();
    }

    $: updatePayload(payload);
    $: updateValues(values);
    let item: V1.Product | undefined = undefined;
    $: {
        item =
            PRODUCT.nogo ? undefined :
            ITEMS.length == 1 ? ITEMS[0] :
            payload ? ITEMS.find(item => item.payload === payload) : undefined;
    };
    $: itemChanged(item, amount || 1);
    let readyIn: string | undefined = undefined;
    let inStock: number | undefined = undefined;
    $: {
        ({readyIn, inStock} =
            itemReady(item, $country, PRODUCT.ready, ITEMS, AVAILABILITY, READY_IN_STOCK) ??
            ({} as any));
    };
    $: seats = computeSeats(values.size, PRODUCT);

    $: variables = (VARIABLES && Object.entries(VARIABLES)) ?? [];
</script>

{#if betterAlternative}
    <Information
        dismissible
        className="better-alternative"
        on:close={() => betterAlternative = undefined}
        on:appear={() => eventOfferAlternative()}
    >
        <p>
            <strong>{t('tip', SCOPE)}:</strong>
            <span>{@html t('save_alt_size_html', {...betterAlternative, ...SCOPE})}</span>
        </p>
        <div>
            <button
                on:click|preventDefault={() => {
                    eventRejectAlternative();
                    betterAlternative = false;
                }}
            >{@html t('no_stay_size_html', {...betterAlternative, ...SCOPE})}</button>
            <button
                on:click|preventDefault={() => {
                    eventAcceptAlternative();
                    selectBetterAlternative();
                    betterAlternative = undefined;
                }}
            >{@html t('yes_alt_size_html', {...betterAlternative, ...SCOPE})}</button>
        </div>
    </Information>
{:else}
    {#each variables as [dimension, variable]}
        {#if variable.customize?.information && showInformation[dimension]}
            <Information
                dismissible
                on:close={() => showInformation[dimension] = false}
            >{variable.customize.information[locale]}</Information>
        {/if}
    {/each}
{/if}

{#each variables as [dimension, variable]}
    {@const {fixed, custom} = splitOptions(variable.options)}
    {@const {customize} = variable}
    {@const skip = (fixed.length + custom.length <= 1) && (
        dimension === 'material' && PRODUCT.material ||
        dimension === 'size' && dimensions
    )}
    {#if !skip}
    <div class="{dimension}"><div>
        <strong>{variable.title}:</strong>
        <div class="options-wrap">
            {#each fixed as {key, title}, index}
            <span>
                <label>
                    <input type="radio"
                        name={dimension} value={key}
                        class="option"
                        bind:group={values[dimension]}
                        on:click={() => customizing[dimension] = false}
                    >
                    {typeof title === 'string' ? title : title.short}
                </label>{#if (index < fixed.length-1) || (customize && custom.length > 0) || (PRODUCT.inquiry === dimension)}, {/if}
            </span>
            <wbr>
            {/each}
            {#if customize && custom.length > 0}
                <!-- svelte-ignore a11y-click-events-have-key-events -->
                <label
                    on:click={() => customizing[dimension] = true}
                >
                    {#if customizing[dimension]}
                    <input
                        type="number"
                        min={customize.minimum} max={customize.maximum} step={customize.step}
                        bind:value={values[dimension]}
                        on:focus={$isPhone ? selectInputText : null}
                    >
                    {customize.prompt[locale]}
                    {:else}
                    {customize.choose[locale]}
                    {/if}
                </label>
            {/if}
            {#if PRODUCT.inquiry === dimension}
                <button class="inquiry" on:click|preventDefault={sendInquiry}>
                    {t(`inquiry.prompt.${PRODUCT.inquiry}`, SCOPE)}
                </button>
            {/if}
        </div>
    {#if variable.customize?.information}
        <button class="info"
            on:click|preventDefault={() => showInformation[dimension] = !showInformation[dimension]}
        ></button>
    {/if}
    </div></div>
    {/if}
{/each}

{#if PRODUCT.inquiry === true}
<div class="inquiry"><div>
    <button class="inquiry" on:click|preventDefault={sendInquiry}>
        {t('inquiry.prompt.default', SCOPE)}
    </button>
</div></div>
{/if}

{#if PRODUCT.inquiry === 'limited-edition'}
<div class="inquiry"><div>
    <button class="inquiry" on:click|preventDefault={sendInquiry}>
        {t('inquiry.prompt.limited-edition', SCOPE)}
    </button>
</div></div>
{/if}

{#if amount !== false}
<div class="amount"><div>
    <strong>{t('amount', SCOPE)}:</strong>
    <span>{amount}</span>
    <button on:click|preventDefault={decrementAmount}>–</button>
    <button on:click|preventDefault={incrementAmount}>+</button>
</div></div>
{/if}

{#if PRODUCT.nogo}
<div class="unavailable"><div>
    <span>{t('nogo', SCOPE)}</span>
</div></div>
{:else if !item && !PRODUCT.nogo}
<div class="unavailable"><div>
    <span>{t('unavailable', SCOPE)}</span>
</div></div>
{:else}
    {#if additional}
    <div class="information"><div>
        {#if additional.title}<strong>{additional.title}:</strong>{/if}
        <span>{additional.text}</span>
    </div></div>
    {/if}

    {#if dimensions}
    <div class="dimensions"><div>
        <strong>{t('dimensions', SCOPE)} <small>({t(`dim.${dimensions.key}`, SCOPE)})</small>:</strong>
        <span>{dimensions.title}</span>
    </div></div>
    {/if}

    {#if PRODUCT.material}
    <div class="material only-desktop"><div>
        <strong>{t('material', SCOPE)}:</strong>
        <span>{PRODUCT.material[locale]}</span>
    </div></div>
    {/if}

    {#if seats}
        <div class="seats"><div>
            <strong>{t('seats', SCOPE)}:</strong>
            <span>{seats}</span>
        </div></div>
    {/if}

    <div class="shipping"><div>
        <strong>
        {#if $country && COUNTRY_NAMES[$country]}
            {#if readyIn}
                {#if inStock}{t(inStock < FEW_IN_STOCK ? 'few-in-stock' : 'in-stock', SCOPE)} &ndash; {/if}
                {t(PRODUCT.shipping_type == 'parcel' ? 'shipping-parcel' : 'shipping-forwarder', SCOPE)}
                <span class="nowrap">
                    (<button on:click|preventDefault={showShippingPopup}>
                        {COUNTRY_NAMES[$country]}
                    </button>)
                </span>:
                <span>
                    {formatReadyness(readyIn, $country)}
                </span>
            {:else}
                {t('shipping', SCOPE)}
                <span class="nowrap">
                    (<button on:click|preventDefault={showShippingPopup}>
                        {COUNTRY_NAMES[$country]}
                    </button>)
                </span>
            {/if}
        {:else}
            <button on:click|preventDefault={showShippingPopup}>
                {t('shipping', SCOPE)}
            </button>
        {/if}
        </strong>
    </div></div>
{/if}
